/**
 * <collect-list> — the parts a shopper wants to fetch from their dealer, and the summary
 * they send themselves.
 *
 * Step 2 and 3 of the collect flow (step 1, choosing the dealer, is the availability
 * dialog). Opens a <dialog> that lists what they have added, looks each article up at the
 * chosen dealer through the same App Proxy the panel uses, and lets them mail themselves the
 * dealer's details together with the list.
 *
 * A part the dealer does not carry stays on the list and says so. That is deliberate: the
 * shopper asked for these parts, and a list that silently drops what is missing is worse
 * than one that tells them what to expect. Nothing here reserves anything — our dealers are
 * independent resellers, so their stock is information we display, not stock we can hold.
 *
 * Interface, all from the markup the block renders:
 *   [data-collect-open]     opens the dialog; carries the item count
 *   [data-collect-count]    the count's text node
 *   [data-collect-dialog]   the <dialog>
 *   [data-collect-dealer]   where the chosen dealer (or the prompt to pick one) renders
 *   [data-collect-items]    the list
 *   [data-collect-empty]    shown instead of the list when nothing is on it
 *   [data-collect-form]     the email form; [data-collect-email] its input
 *   data-endpoint           per-article stock (App Proxy)
 *   data-submit-endpoint    optional: POSTs the summary. Empty → a mailto: is opened
 *                           instead, which needs no backend and stores no address.
 *
 * The whole element hides itself when localStorage is unavailable or the list is empty, so
 * it costs nothing on a page nobody is collecting from.
 */
if (!customElements.get('collect-list')) {
  const LOOKUP_TIMEOUT_MS = 8000;

  function timeoutSignal(ms) {
    try {
      return AbortSignal.timeout(ms);
    } catch (e) {
      return undefined;
    }
  }

  customElements.define(
    'collect-list',
    class CollectList extends HTMLElement {
      connectedCallback() {
        this.opener = this.querySelector('[data-collect-open]');
        this.countEl = this.querySelector('[data-collect-count]');
        this.dialog = this.querySelector('[data-collect-dialog]');
        this.dealerEl = this.querySelector('[data-collect-dealer]');
        this.itemsEl = this.querySelector('[data-collect-items]');
        this.emptyEl = this.querySelector('[data-collect-empty]');
        this.form = this.querySelector('[data-collect-form]');
        this.emailEl = this.querySelector('[data-collect-email]');
        if (!this.opener || !this.dialog || !this.itemsEl) return;
        this.store = window.ValerydDealer;
        if (!this.store) return;

        this.onOpen = () => this.open();
        this.onChange = () => this.renderCount();
        this.onDialogClick = (event) => {
          const remove = event.target.closest('[data-collect-remove]');
          if (remove) return this.store.remove(remove.dataset.collectRemove);
          if (event.target.closest('[data-collect-clear]')) return this.store.clearList();
          if (event.target === this.dialog) this.dialog.close();
        };
        this.onSubmit = (event) => {
          event.preventDefault();
          this.send();
        };

        this.opener.addEventListener('click', this.onOpen);
        this.dialog.addEventListener('click', this.onDialogClick);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        document.addEventListener('dealer:changed', this.onChange);
        // Hidden until ValerydDealer.country() has named a country whose dealers this page shows.
        // A list started on another market does not bring its dealer claims to one without them.
        this.country = null;
        this.renderCount();
        this.store.country(this).then((country) => {
          this.country = country;
          this.renderCount();
        });
      }

      disconnectedCallback() {
        if (this.opener) this.opener.removeEventListener('click', this.onOpen);
        if (this.dialog) this.dialog.removeEventListener('click', this.onDialogClick);
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        document.removeEventListener('dealer:changed', this.onChange);
        if (this.dialog && this.dialog.open) this.dialog.close();
      }

      // The opener carries the count and disappears with an empty list, so the control never
      // invites a shopper into an empty room.
      renderCount() {
        const n = this.store.count();
        if (this.countEl) this.countEl.textContent = String(n);
        this.hidden = n === 0 || !this.country;
        if (n === 0 && this.dialog.open) this.dialog.close();
        else if (this.dialog.open) this.renderBody();
      }

      open() {
        this.renderBody();
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
        this.lookup();
      }

      renderBody() {
        this.renderDealer();
        const items = this.store.list();
        if (this.emptyEl) this.emptyEl.hidden = items.length > 0;
        this.itemsEl.replaceChildren();
        // Keep each status cell by SKU rather than looking it up by a selector built from one:
        // an article number is merchant data and can carry anything a CSS selector would need
        // escaped, and a throw inside the lookup below is swallowed by allSettled — the row
        // would simply sit on "Checking…" forever.
        this.cells = new Map();
        const frag = document.createDocumentFragment();
        items.forEach((item) => frag.appendChild(this.row(item)));
        this.itemsEl.appendChild(frag);
      }

      renderDealer() {
        if (!this.dealerEl) return;
        const chosen = this.store.read();
        this.dealerEl.replaceChildren();
        if (!chosen) {
          const p = document.createElement('p');
          p.className = 'collect__prompt';
          p.textContent = this.dataset.pickDealer || '';
          this.dealerEl.appendChild(p);
          return;
        }
        const d = chosen.dealer;
        const name = document.createElement('p');
        name.className = 'collect__dealer-name';
        name.textContent = [d.name, d.city].filter(Boolean).join(' – ');
        this.dealerEl.appendChild(name);

        const meta = [d.postcode, d.city].filter(Boolean).join(' ');
        if (meta) {
          const p = document.createElement('p');
          p.className = 'collect__dealer-meta';
          p.textContent = meta;
          this.dealerEl.appendChild(p);
        }
        if (d.phone) {
          const a = document.createElement('a');
          a.className = 'collect__dealer-phone';
          a.href = 'tel:' + d.phone;
          a.textContent = d.phone;
          this.dealerEl.appendChild(a);
        }
      }

      row(item) {
        const li = document.createElement('li');
        li.className = 'collect__row';
        li.dataset.sku = item.sku;

        const body = document.createElement('span');
        body.className = 'collect__body';
        const title = document.createElement('span');
        title.className = 'collect__title';
        title.textContent = item.title;
        body.appendChild(title);
        const sku = document.createElement('span');
        sku.className = 'collect__sku';
        sku.textContent = (this.dataset.artnoLabel ? this.dataset.artnoLabel + ' ' : '') + item.sku;
        body.appendChild(sku);
        li.appendChild(body);

        const state = document.createElement('span');
        state.className = 'collect__state';
        state.dataset.collectState = item.sku;
        state.textContent = this.dataset.checking || '';
        if (this.cells) this.cells.set(item.sku, state);
        li.appendChild(state);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'collect__remove';
        remove.dataset.collectRemove = item.sku;
        remove.setAttribute('aria-label', (this.dataset.removeLabel || 'Remove') + ' ' + item.title);
        remove.textContent = '×';
        li.appendChild(remove);
        return li;
      }

      // One request per article, because that is what the proxy answers: stock for an article
      // across dealers. They run together and each row settles on its own, so a slow or failed
      // article never holds up the rest of the list.
      async lookup() {
        const chosen = this.store.read();
        const items = this.store.list();
        if (!this.dataset.endpoint || !items.length) return;
        const base = this.dataset.endpoint;
        const country = (chosen && chosen.dealer.country) || this.country;
        if (!country) return;

        await Promise.allSettled(
          items.map(async (item) => {
            const cell = this.cells && this.cells.get(item.sku);
            if (!cell) return;
            try {
              const sep = base.indexOf('?') === -1 ? '?' : '&';
              const url = base + sep + 'artno=' + encodeURIComponent(item.sku) + '&country=' + encodeURIComponent(country);
              const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: timeoutSignal(LOOKUP_TIMEOUT_MS) });
              if (!res.ok) throw new Error(String(res.status));
              const data = await res.json();
              this.fillState(cell, data, chosen);
            } catch (error) {
              // An article we could not check says so rather than claiming absence.
              cell.textContent = this.dataset.unknown || '';
              cell.className = 'collect__state collect__state--unknown';
            }
          })
        );
      }

      fillState(cell, data, chosen) {
        const stores = Array.isArray(data && data.stores) ? data.stores : [];
        if (!chosen) {
          const anywhere = stores.filter((s) => s.inStock).length;
          cell.textContent = anywhere
            ? (this.dataset.atDealers || '%COUNT%').replace('%COUNT%', anywhere)
            : this.dataset.notStocked || '';
          cell.className = 'collect__state' + (anywhere ? ' collect__state--in' : '');
          return;
        }
        const mine = stores.find((s) => s.name === chosen.dealer.name && s.city === chosen.dealer.city);
        const qty = mine && mine.inStock ? mine.quantity : 0;
        cell.textContent = qty
          ? (this.dataset.inStock || '%COUNT%').replace('%COUNT%', qty)
          : this.dataset.notStocked || '';
        cell.className = 'collect__state' + (qty ? ' collect__state--in' : '');
      }

      // Without a configured endpoint this opens the shopper's own mail client with everything
      // filled in. It needs no backend and stores no address anywhere — which is why it is the
      // default rather than a stub that pretends to send.
      send() {
        const chosen = this.store.read();
        const items = this.store.list();
        if (!items.length) return;
        const email = this.emailEl ? this.emailEl.value.trim() : '';
        const dealer = chosen ? chosen.dealer : null;
        const lines = items.map((i) => `- ${i.title} (${i.sku}) x${i.quantity || 1}`);
        const dealerLines = dealer
          ? [dealer.name, [dealer.postcode, dealer.city].filter(Boolean).join(' '), dealer.phone].filter(Boolean)
          : [];
        const subject = this.dataset.mailSubject || 'Parts to collect';
        const body = [dealerLines.join('\n'), '', lines.join('\n')].join('\n').trim();

        const endpoint = this.dataset.submitEndpoint;
        if (endpoint) {
          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, dealer, items }),
          })
            .then((res) => this.afterSend(res.ok))
            .catch(() => this.afterSend(false));
          return;
        }
        window.location.href =
          'mailto:' + encodeURIComponent(email) + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
        this.afterSend(true);
      }

      afterSend(ok) {
        const note = this.querySelector('[data-collect-note]');
        if (!note) return;
        note.textContent = ok ? this.dataset.sent || '' : this.dataset.sendFailed || '';
        note.hidden = false;
      }
    }
  );
}
